"use client"

import {
  createContext,
  use,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react"

export const ToastSelectionContext = createContext<
  ((element: HTMLElement) => () => void) | null
>(null)

export function useToastSelectionBar() {
  const ref = useRef<HTMLDivElement>(null)
  const register = use(ToastSelectionContext)
  useLayoutEffect(() => {
    if (ref.current) return register?.(ref.current)
  }, [register])
  return ref
}

export function useToastSelectionClearance() {
  const [bars, setBars] = useState<HTMLElement[]>([])
  const [bottom, setBottom] = useState(16)
  const register = useCallback((element: HTMLElement) => {
    setBars((current) => [...current, element])
    return () => {
      setBars((current) => current.filter((bar) => bar !== element))
    }
  }, [])

  useLayoutEffect(() => {
    const measure = () => {
      // Horizontal placement remains owned by Sonner and the existing chat offset.
      const toaster = document.querySelector<HTMLElement>(
        "[data-sonner-toaster]"
      )
      const toastRect = toaster?.getBoundingClientRect()
      let clearance = 16
      for (const bar of bars) {
        const rect = bar.getBoundingClientRect()
        if (
          rect.height > 0 &&
          rect.bottom > 0 &&
          rect.top < window.innerHeight &&
          (!toastRect ||
            (rect.right > toastRect.left && rect.left < toastRect.right))
        ) {
          clearance = Math.max(clearance, window.innerHeight - rect.top + 16)
        }
      }
      setBottom(clearance)
    }
    measure()
    if (bars.length === 0) return
    const resize = new ResizeObserver(measure)
    for (const bar of bars) resize.observe(bar)
    const toaster = document.querySelector("[data-sonner-toaster]")
    if (toaster) resize.observe(toaster)
    // Chat width is published on the root style, including while resizing it.
    const rootStyle = new MutationObserver(measure)
    rootStyle.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style"],
    })
    window.addEventListener("resize", measure)
    window.addEventListener("scroll", measure, true)
    return () => {
      resize.disconnect()
      rootStyle.disconnect()
      window.removeEventListener("resize", measure)
      window.removeEventListener("scroll", measure, true)
    }
  }, [bars])

  return { bottom, register }
}
